import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { History, AlertTriangle, ChevronDown, ChevronUp, RefreshCw, X, Calendar, MapPin, Wrench, Undo2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate, formatDateTime } from "@/lib/dates";
import { Input } from "@/components/ui/input";
import { buildDamageRegister, damageStatusText, normalizeSeverity, severityLabel, viewLabel } from "@/lib/damageRegister";
import { markDamageRepaired, undoDamageRepair } from "@/lib/damageData";
import { loggedInUserName } from "@/lib/vehicleChecksData";
import { localIsoDay } from "@/lib/vehicleChecks";
import DamageReportButtons from "./DamageReportButtons";

type Vehicle = {
  vehicle_number: string;
  make?: string;
  model?: string;
};

type Inspection = {
  id: string;
  vehicle_number: string;
  driver_name: string;
  created_at: string;
  overall_status: string;
  inspection_type: string | null;
  completed_at?: string | null;
  progress_pct?: number | null;
};

type InspectionPhoto = {
  id: string;
  inspection_id: string;
  photo_type: string;
  storage_path: string;
};

type DamageMarker = {
  id: string;
  photo_id: string;
  x_percent: number;
  y_percent: number;
  damage_type: string;
  severity: string;
  notes: string | null;
  created_at: string;
  repaired_on?: string | null;
  repaired_by?: string | null;
};

type DamageWithContext = DamageMarker & {
  inspection_id: string;
  inspection_date: string;
  driver_name: string;
  photo_type: string;
  storage_path: string;
  is_new: boolean;
};

const BUCKET_VEHICLE_INSPECTIONS = "vehicle-inspections";

const SEVERITY_COLORS: Record<string, string> = {
  minor: "text-yellow-400 border-yellow-400/50 bg-yellow-500/10",
  moderate: "text-orange-400 border-orange-400/50 bg-orange-500/10",
  severe: "text-red-400 border-red-400/50 bg-red-500/10",
};

const DAMAGE_TYPE_ICONS: Record<string, string> = {
  scratch: "Scratch",
  dent: "Dent",
  crack: "Crack",
  paint_damage: "Paint Damage",
  rust: "Rust",
  other: "Other",
};

function getPhotoUrl(storagePath: string): string {
  if (!storagePath) return "";
  const { data } = supabase.storage
    .from(BUCKET_VEHICLE_INSPECTIONS)
    .getPublicUrl(storagePath);
  return data?.publicUrl || "";
}

// Check if two damage markers are approximately the same location
function isSameLocation(m1: DamageMarker, m2: DamageMarker, threshold = 5): boolean {
  const dx = Math.abs(m1.x_percent - m2.x_percent);
  const dy = Math.abs(m1.y_percent - m2.y_percent);
  return dx < threshold && dy < threshold;
}

interface DamageHistoryTimelineProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleNumber?: string;
  /** Whether this user may record repairs. */
  canEdit?: boolean;
}

const DamageHistoryTimeline: React.FC<DamageHistoryTimelineProps> = ({
  isOpen,
  onClose,
  vehicleNumber: initialVehicle,
  canEdit = true,
}) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<string>(initialVehicle || "");
  const [loading, setLoading] = useState(false);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [damageData, setDamageData] = useState<DamageWithContext[]>([]);
  const [expandedInspections, setExpandedInspections] = useState<Set<string>>(new Set());
  const [selectedPhoto, setSelectedPhoto] = useState<{ url: string; type: string; markers: DamageWithContext[] } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [repairingKey, setRepairingKey] = useState<string | null>(null);
  const [repairDate, setRepairDate] = useState(() => localIsoDay(new Date()));

  // Load vehicles
  useEffect(() => {
    async function loadVehicles() {
      const { data, error } = await supabase
        .from("vehicles")
        .select("vehicle_number, make, model")
        .order("vehicle_number");

      if (!error && data) {
        setVehicles(data);
      }
    }
    if (isOpen) {
      loadVehicles();
    }
  }, [isOpen]);

  // Set initial vehicle when prop changes
  useEffect(() => {
    if (initialVehicle) {
      setSelectedVehicle(initialVehicle);
    }
  }, [initialVehicle]);

  // Load damage history when vehicle is selected
  useEffect(() => {
    async function loadDamageHistory() {
      if (!selectedVehicle) {
        setInspections([]);
        setDamageData([]);
        return;
      }

      setLoading(true);
      try {
        // Get all inspections for this vehicle
        const { data: rawInspections, error: inspError } = await supabase
          .from("vehicle_inspections")
          .select("id, vehicle_number, driver_name, created_at, overall_status, inspection_type, completed_at, progress_pct")
          .eq("vehicle_number", selectedVehicle)
          .order("created_at", { ascending: false });

        if (inspError) throw inspError;
        // An inspection still being worked on isn't part of the record until it is finished.
        const inspectionsData = (rawInspections || []).filter((i) => i.completed_at != null || i.progress_pct == null);
        setInspections(inspectionsData || []);

        if (!inspectionsData || inspectionsData.length === 0) {
          setDamageData([]);
          return;
        }

        // Get all photos for these inspections
        const inspectionIds = inspectionsData.map((i) => i.id);
        const { data: photosData, error: photosError } = await supabase
          .from("vehicle_inspection_photos")
          .select("id, inspection_id, photo_type, storage_path")
          .in("inspection_id", inspectionIds);

        if (photosError) throw photosError;

        if (!photosData || photosData.length === 0) {
          setDamageData([]);
          return;
        }

        // Get all damage markers for these photos
        const photoIds = photosData.map((p) => p.id);
        const { data: markersData, error: markersError } = await supabase
          .from("vehicle_inspection_damage_markers")
          .select("*")
          .in("photo_id", photoIds)
          .order("created_at", { ascending: true });

        if (markersError) throw markersError;

        // Build damage data with context
        const damageWithContext: DamageWithContext[] = [];
        const previousMarkers: DamageMarker[] = [];

        // Sort inspections by date (oldest first for tracking new vs existing)
        const sortedInspections = [...inspectionsData].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        for (const inspection of sortedInspections) {
          const inspectionPhotos = photosData.filter((p) => p.inspection_id === inspection.id);

          for (const photo of inspectionPhotos) {
            const photoMarkers = (markersData || []).filter((m) => m.photo_id === photo.id);

            for (const marker of photoMarkers) {
              // Check if this is a new damage or existing
              const isNew = !previousMarkers.some(
                (pm) =>
                  pm.damage_type === marker.damage_type &&
                  isSameLocation(pm, marker)
              );

              damageWithContext.push({
                ...marker,
                inspection_id: inspection.id,
                inspection_date: inspection.created_at,
                driver_name: inspection.driver_name,
                photo_type: photo.photo_type,
                storage_path: photo.storage_path,
                is_new: isNew,
              });

              // Add to previous markers for tracking
              previousMarkers.push(marker);
            }
          }
        }

        // Sort by date descending for display
        damageWithContext.sort(
          (a, b) => new Date(b.inspection_date).getTime() - new Date(a.inspection_date).getTime()
        );

        setDamageData(damageWithContext);
      } catch (e: any) {
        toast({
          title: "Error loading damage history",
          description: e.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    }

    loadDamageHistory();
  }, [selectedVehicle, reloadKey]);

  // Group damage by inspection
  const damageByInspection = useMemo(() => {
    const grouped: Record<string, DamageWithContext[]> = {};
    for (const damage of damageData) {
      if (!grouped[damage.inspection_id]) {
        grouped[damage.inspection_id] = [];
      }
      grouped[damage.inspection_id].push(damage);
    }
    return grouped;
  }, [damageData]);

  // Get unique damage types and severities for summary
  const damageSummary = useMemo(() => {
    const types: Record<string, number> = {};
    const severities: Record<string, number> = {};
    let newCount = 0;

    for (const damage of damageData) {
      types[damage.damage_type] = (types[damage.damage_type] || 0) + 1;
      const sev = normalizeSeverity(damage.severity);
      severities[sev] = (severities[sev] || 0) + 1;
      if (damage.is_new) newCount++;
    }

    return { types, severities, newCount, total: damageData.length };
  }, [damageData]);

  // Each piece of damage across all the checks: when first seen, how it changed, whether it was repaired.
  const register = useMemo(
    () =>
      buildDamageRegister(
        damageData.map((d) => ({
          id: d.id,
          inspection_id: d.inspection_id,
          inspection_at: d.inspection_date,
          inspection_type: inspections.find((i) => i.id === d.inspection_id)?.inspection_type ?? null,
          inspected_by: d.driver_name,
          photo_id: d.photo_id,
          photo_type: d.photo_type,
          storage_path: d.storage_path,
          x_percent: Number(d.x_percent),
          y_percent: Number(d.y_percent),
          damage_type: d.damage_type,
          severity: d.severity,
          notes: d.notes,
          repaired_on: d.repaired_on ?? null,
          repaired_by: d.repaired_by ?? null,
        }))
      ),
    [damageData, inspections]
  );

  const recordRepair = async (markerIds: string[]) => {
    try {
      await markDamageRepaired(markerIds, repairDate, loggedInUserName());
      toast({ title: "Repair recorded" });
      setRepairingKey(null);
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      toast({ title: "Couldn't record the repair", description: e?.message ?? String(e), variant: "destructive" });
    }
  };

  const removeRepair = async (markerIds: string[]) => {
    try {
      await undoDamageRepair(markerIds);
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      toast({ title: "Couldn't undo the repair", description: e?.message ?? String(e), variant: "destructive" });
    }
  };

  const toggleInspection = (inspectionId: string) => {
    setExpandedInspections((prev) => {
      const next = new Set(prev);
      if (next.has(inspectionId)) {
        next.delete(inspectionId);
      } else {
        next.add(inspectionId);
      }
      return next;
    });
  };

  const openPhotoWithMarkers = (damage: DamageWithContext) => {
    const relatedMarkers = damageData.filter(
      (d) => d.photo_id === damage.photo_id && d.inspection_id === damage.inspection_id
    );
    setSelectedPhoto({
      url: getPhotoUrl(damage.storage_path),
      type: damage.photo_type,
      markers: relatedMarkers,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-amber-500 flex items-center gap-2">
            <History className="w-5 h-5" />
            Damage History Timeline
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Vehicle selector */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a vehicle..." />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.vehicle_number} value={v.vehicle_number}>
                      {v.vehicle_number} - {v.make} {v.model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DamageReportButtons vehicleNumber={selectedVehicle || null} />
            {loading && (
              <RefreshCw className="w-5 h-5 animate-spin text-amber-500" />
            )}
          </div>

          {selectedVehicle && !loading && (
            <>
              {/* Summary cards */}
              {damageData.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="bg-gray-800/50 border-gray-700">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-amber-500">{damageSummary.total}</div>
                      <div className="text-xs text-gray-400">Total Damage Markers</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-800/50 border-gray-700">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-red-400">{damageSummary.newCount}</div>
                      <div className="text-xs text-gray-400">New Damage Found</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-800/50 border-gray-700">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-blue-400">{inspections.length}</div>
                      <div className="text-xs text-gray-400">Total Inspections</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-800/50 border-gray-700">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-orange-400">
                        {damageSummary.severities["severe"] || 0}
                      </div>
                      <div className="text-xs text-gray-400">Severe Damage</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Damage type breakdown */}
              {damageData.length > 0 && (
                <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700">
                  <div className="text-sm font-semibold text-amber-500 mb-3">Damage Type Breakdown</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(damageSummary.types).map(([type, count]) => (
                      <Badge key={type} variant="outline" className="text-gray-300 border-gray-600">
                        {DAMAGE_TYPE_ICONS[type] || type}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Each piece of damage: first seen, changes, repaired */}
              {register.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-amber-500">Damage items: how each has progressed</div>
                  {register.map((item) => {
                    const sev =
                      item.firstSeverity === item.latestSeverity
                        ? severityLabel(item.latestSeverity)
                        : severityLabel(item.firstSeverity) + " \u2192 " + severityLabel(item.latestSeverity);
                    const statusClass =
                      item.status === "repaired"
                        ? "text-green-400 border-green-400/50"
                        : item.reappeared
                        ? "text-red-400 border-red-400/50"
                        : "text-orange-400 border-orange-400/50";
                    return (
                      <div key={item.key} className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-gray-700 bg-gray-800/30 p-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-gray-200">
                              {viewLabel(item.view)} &middot; {item.damageType}
                            </span>
                            <Badge variant="outline" className={cn("text-xs", statusClass)}>
                              {damageStatusText(item, formatDate)}
                            </Badge>
                            {item.severityTrend === "worse" && (
                              <Badge variant="outline" className="text-xs text-red-400 border-red-400/50">
                                Got worse
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-gray-400">
                            First seen {formatDate(item.firstSeenAt)} &middot; last seen {formatDate(item.lastSeenAt)} &middot; seen {item.sightings.length}&times;
                            {sev ? " \u00b7 " + sev : ""}
                          </div>
                          {item.notes.length > 0 && <div className="mt-1 text-xs text-gray-300">{item.notes.join(" \u00b7 ")}</div>}
                        </div>

                        {canEdit &&
                          (repairingKey === item.key ? (
                            <div className="flex items-center gap-2">
                              <Input type="date" value={repairDate} max={localIsoDay(new Date())} onChange={(e) => setRepairDate(e.target.value)} className="h-8 w-40" />
                              <Button size="sm" onClick={() => recordRepair(item.markerIds)} disabled={!repairDate}>
                                Save repair
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setRepairingKey(null)}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              {item.status === "present" && (
                                <Button size="sm" variant="outline" onClick={() => setRepairingKey(item.key)}>
                                  <Wrench className="mr-1 h-3.5 w-3.5" /> Mark repaired
                                </Button>
                              )}
                              {item.repairedOn && (
                                <Button size="sm" variant="ghost" onClick={() => removeRepair(item.markerIds)} title="Remove the repair record">
                                  <Undo2 className="mr-1 h-3.5 w-3.5" /> Undo repair
                                </Button>
                              )}
                            </div>
                          ))}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Timeline */}
              {damageData.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No damage markers found for this vehicle.</p>
                  <p className="text-sm mt-2">
                    Damage markers are added during inspections when issues are identified.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm font-semibold text-amber-500">Inspection Timeline</div>

                  {inspections.map((inspection) => {
                    const damages = damageByInspection[inspection.id] || [];
                    const isExpanded = expandedInspections.has(inspection.id);
                    const newDamageCount = damages.filter((d) => d.is_new).length;

                    return (
                      <div
                        key={inspection.id}
                        className="border border-gray-700 rounded-lg overflow-hidden"
                      >
                        {/* Inspection header */}
                        <button
                          onClick={() => toggleInspection(inspection.id)}
                          className="w-full flex items-center justify-between p-4 bg-gray-800/50 hover:bg-gray-800/70 transition-colors text-left"
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-gray-300">
                              <Calendar className="w-4 h-4" />
                              <span className="font-medium">{formatDateTime(inspection.created_at)}</span>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {inspection.inspection_type === "admin" ? "Admin" : "Driver"}
                            </Badge>
                            <span className="text-sm text-gray-400">by {inspection.driver_name}</span>
                          </div>

                          <div className="flex items-center gap-3">
                            {damages.length > 0 && (
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    "text-xs",
                                    damages.length > 0 ? "text-orange-400 border-orange-400/50" : ""
                                  )}
                                >
                                  {damages.length} damage{damages.length !== 1 ? "s" : ""}
                                </Badge>
                                {newDamageCount > 0 && (
                                  <Badge className="text-xs bg-red-500/20 text-red-400 border-red-400/50">
                                    {newDamageCount} NEW
                                  </Badge>
                                )}
                              </div>
                            )}
                            {damages.length === 0 && (
                              <Badge variant="outline" className="text-xs text-green-400 border-green-400/50">
                                No damage
                              </Badge>
                            )}
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </button>

                        {/* Expanded damage list */}
                        {isExpanded && damages.length > 0 && (
                          <div className="p-4 bg-gray-900/50 border-t border-gray-700 space-y-3">
                            {damages.map((damage) => (
                              <div
                                key={damage.id}
                                className={cn(
                                  "flex items-start gap-4 p-3 rounded-lg border",
                                  damage.is_new
                                    ? "bg-red-500/10 border-red-500/30"
                                    : "bg-gray-800/30 border-gray-700"
                                )}
                              >
                                {/* Photo thumbnail */}
                                <button
                                  onClick={() => openPhotoWithMarkers(damage)}
                                  className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border border-gray-600 hover:border-amber-500 transition-colors"
                                >
                                  <img
                                    src={getPhotoUrl(damage.storage_path)}
                                    alt={damage.photo_type}
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-1 py-0.5 text-center">
                                    {damage.photo_type.replace("exterior_", "").toUpperCase()}
                                  </div>
                                  {/* Marker position indicator */}
                                  <div
                                    className="absolute w-3 h-3 bg-red-500 rounded-full border-2 border-white transform -translate-x-1/2 -translate-y-1/2"
                                    style={{
                                      left: `${damage.x_percent}%`,
                                      top: `${damage.y_percent}%`,
                                    }}
                                  />
                                </button>

                                {/* Damage details */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    {damage.is_new && (
                                      <Badge className="text-[10px] bg-red-500 text-white px-1.5 py-0">
                                        NEW
                                      </Badge>
                                    )}
                                    <span className="font-medium text-gray-200">
                                      {DAMAGE_TYPE_ICONS[damage.damage_type] || damage.damage_type}
                                    </span>
                                    <Badge
                                      variant="outline"
                                      className={cn("text-xs", SEVERITY_COLORS[normalizeSeverity(damage.severity)])}
                                    >
                                      {damage.severity}
                                    </Badge>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                                    <MapPin className="w-3 h-3" />
                                    <span>
                                      Position: {Math.round(damage.x_percent)}%, {Math.round(damage.y_percent)}%
                                    </span>
                                  </div>

                                  {damage.notes && (
                                    <p className="text-sm text-gray-300 mt-1">{damage.notes}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {isExpanded && damages.length === 0 && (
                          <div className="p-4 bg-gray-900/50 border-t border-gray-700 text-center text-gray-400 text-sm">
                            No damage markers recorded in this inspection.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {!selectedVehicle && (
            <div className="text-center py-12 text-gray-400">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Select a vehicle to view its damage history timeline.</p>
            </div>
          )}
        </div>

        {/* Photo viewer modal */}
        {selectedPhoto && (
          <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
            <DialogContent className="max-w-3xl bg-gray-900 border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-amber-500">
                  {selectedPhoto.type.replace("exterior_", "").toUpperCase()} View
                </DialogTitle>
              </DialogHeader>
              <div className="relative">
                <img
                  src={selectedPhoto.url}
                  alt={selectedPhoto.type}
                  className="w-full rounded-lg"
                />
                {/* Render markers on the photo */}
                {selectedPhoto.markers.map((marker) => (
                  <div
                    key={marker.id}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
                    style={{
                      left: `${marker.x_percent}%`,
                      top: `${marker.y_percent}%`,
                    }}
                  >
                    <div
                      className={cn(
                        "w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold cursor-pointer",
                        marker.is_new
                          ? "bg-red-500 border-white text-white"
                          : "bg-orange-500 border-white text-white"
                      )}
                    >
                      {marker.is_new ? "!" : "•"}
                    </div>
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                      <div className="bg-gray-800 text-white text-xs rounded-lg p-2 shadow-lg whitespace-nowrap border border-gray-600">
                        <div className="font-semibold">
                          {DAMAGE_TYPE_ICONS[marker.damage_type] || marker.damage_type}
                        </div>
                        <div className={SEVERITY_COLORS[normalizeSeverity(marker.severity)]?.split(" ")[0]}>
                          {marker.severity}
                        </div>
                        {marker.notes && <div className="mt-1 text-gray-300">{marker.notes}</div>}
                        {marker.is_new && (
                          <div className="mt-1 text-red-400 font-semibold">NEW DAMAGE</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setSelectedPhoto(null)}>
                  Close
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DamageHistoryTimeline;
